/*
 * Dealer documents: the Download button DOWNLOADS.
 *
 * Reported 2026-09-05 — "it shouldn't take them away from page it should just
 * download the PDF". Shopify's CDN serves a PDF inline, so following the link
 * replaces the portal with the document. The `download` attribute cannot fix
 * that on its own: browsers ignore it cross-origin, and cdn.shopify.com is a
 * different origin from quellworkspaces.com.
 *
 * So the bytes are fetched and handed back as a blob. That object URL IS
 * same-origin, which is the one case the download attribute honours.
 * cdn.shopify.com sends `access-control-allow-origin: *` (verified against the
 * live CDN, 2026-09-05), so the fetch is allowed.
 *
 * NOTHING HERE MAY COST THE DEALER THIS PAGE. That is the whole point of the
 * change, so every failure path ends in a new tab or in the browser's own
 * handling — never a same-tab navigation.
 *
 * snippets/link-target.liquid decides which links get this treatment.
 */
(function () {
  'use strict';

  // Two sections load this file. They are never on one page today, but a
  // second listener would download everything twice if they ever were.
  if (window.__quellFileDownload) return;
  window.__quellFileDownload = true;

  var SELECTOR = 'a[data-file-download]';
  var BUSY_TEXT = 'Preparing…';
  var PASSTHROUGH = 'data-file-download-passthrough';

  // Safari reads the blob after click() has returned, and saves a 0-byte file
  // if the object URL is revoked straight away.
  var REVOKE_AFTER_MS = 60000;

  function supported() {
    return !!(window.fetch && window.URL && window.URL.createObjectURL);
  }

  function labelOf(link) {
    return link.querySelector('[data-file-download-label]');
  }

  // A click with no visible response is indistinguishable from one that never
  // registered, so the button says what it is doing.
  function setBusy(link) {
    var label = labelOf(link);
    link.setAttribute('data-state', 'busy');
    link.setAttribute('aria-busy', 'true');
    if (label) label.textContent = link.getAttribute('data-file-download-busy') || BUSY_TEXT;
  }

  function clearBusy(link, originalText) {
    var label = labelOf(link);
    link.removeAttribute('data-state');
    link.removeAttribute('aria-busy');
    if (label) label.textContent = originalText;
  }

  function save(blob, filename) {
    var objectUrl = window.URL.createObjectURL(blob);
    var tmp = document.createElement('a');
    tmp.href = objectUrl;
    tmp.download = filename || '';
    tmp.style.display = 'none';
    document.body.appendChild(tmp);
    tmp.click();
    document.body.removeChild(tmp);
    setTimeout(function () { window.URL.revokeObjectURL(objectUrl); }, REVOKE_AFTER_MS);
  }

  /*
   * Tell the dealer API that this dealer took this document.
   *
   * These certificates go straight from the browser to Shopify's CDN, so
   * neither of our servers can observe the download — without this, the sales
   * team cannot see that a dealer pulled a fire report, which is one of the
   * earliest signals that they are specifying a job.
   *
   * WE DO NOT SAY WHO. The payload names the document only. Identity is read
   * from the session by the dealer API, which is the one party that can check
   * it: window.__dealer comes from a cookie script can write, and its own
   * snippet says forging it changes a nav label. A page that could name the
   * dealer could name somebody else's.
   *
   * REPORTED ONLY WHEN THE BYTES ARRIVED. This is called after `save`, so a
   * failed fetch or a blocked popup records nothing. That under-counts the rare
   * fallback, which is the right way round for a baseline: a consistent rule
   * that can be stated beats a fuller number nobody can define.
   *
   * The section says what kind of document it publishes. Silence is deliberate:
   * a page that has not declared itself is not reported, rather than guessed
   * at.
   */
  function report(link) {
    var api = window.__dealerApi;
    if (!api || !link.closest) return;

    var host = link.closest('[data-download-kind]');
    var kind = host && host.getAttribute('data-download-kind');
    if (!kind) return;

    var category = host.getAttribute('data-download-category') || null;

    try {
      window
        .fetch(api + '/api/dealer/download-event', {
          method: 'POST',
          credentials: 'include',
          // The page is not navigating any more, but a dealer who closes the
          // tab straight after saving should still be counted.
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            kind: kind,
            category: category,
            documentName: link.getAttribute('download') || null,
            documentUrl: link.href,
            occurredAt: new Date().toISOString()
          })
        })
        .catch(function (err) {
          // Never surfaced: the dealer has their file, and a telemetry failure
          // is not their problem. Logged at debug so it is still findable.
          console.debug('[FileDownload] could not report the download', err);
        });
    } catch (err) {
      console.debug('[FileDownload] could not report the download', err);
    }
  }

  /*
   * The fetch failed, so the dealer still needs the file.
   *
   * This runs in a promise callback, which is OUTSIDE the user gesture, and
   * Safari and Firefox block a popup opened from there. So the result of
   * window.open is checked rather than assumed: when it is blocked, the link is
   * marked to let the NEXT click through untouched, and that click is a real
   * gesture, so the anchor's own target="_blank" opens the file. Either way the
   * page the dealer is on survives.
   *
   * `noopener` cannot be passed in the feature string here: by spec it makes
   * window.open return null even on success, which would read as blocked every
   * time. It is dropped from the opened window instead.
   */
  function fallback(link, href) {
    var opened = null;
    try {
      opened = window.open(href, '_blank');
    } catch (e) {
      opened = null;
    }
    if (opened) {
      opened.opener = null;
      return;
    }
    link.setAttribute(PASSTHROUGH, '');
  }

  document.addEventListener('click', function (event) {
    var link = event.target && event.target.closest ? event.target.closest(SELECTOR) : null;
    if (!link) return;

    // A modified click is the dealer asking for something specific — a new tab,
    // a Save link as. Leave it to the browser.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    // A retry after a blocked popup. Hand this one to the browser: the anchor's
    // own target="_blank" opens the file and keeps this page.
    if (link.hasAttribute(PASSTHROUGH)) {
      link.removeAttribute(PASSTHROUGH);
      return;
    }

    // With no fetch, the anchor's own target="_blank" already keeps the page.
    if (!supported()) return;

    event.preventDefault();
    if (link.getAttribute('data-state') === 'busy') return;

    var label = labelOf(link);
    var originalText = label ? label.textContent : '';
    var href = link.href;
    var filename = link.getAttribute('download') || '';

    setBusy(link);

    window.fetch(href, { credentials: 'omit', mode: 'cors' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.blob();
      })
      .then(function (blob) {
        save(blob, filename);
        clearBusy(link, originalText);
        report(link);
      })
      .catch(function () {
        clearBusy(link, originalText);
        fallback(link, href);
      });
  });
})();
